import React, { useState, useEffect } from 'react';
import { apiGet, apiPut } from '../utils/apiUtils';
import { FiX, FiDownload, FiCheckCircle, FiCheck, FiAlertCircle, FiUsers, FiShare, FiUserCheck, FiUserX, FiAward, FiNavigation } from 'react-icons/fi';
import UserAvatar from './UserAvatar';

/**
 * @file TestResultsModal.jsx
 * @description Admin modal used to view the overall results of a batch test.
 *              Displays a leaderboard-style list of students, their attendance, marks, 
 *              and proctoring violations. Allows the admin to publish results and export as CSV.
 * @module components/TestResultsModal
 */

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component TestResultsModal
 * @description Renders the comprehensive admin view for batch test results.
 * 
 * @param {Object} props
 * @param {Object} props.test - The test data object.
 * @param {Function} props.onClose - Callback function to close the modal.
 * @param {Function} props.onPublishSuccess - Callback function triggered after successfully publishing results.
 * @returns {JSX.Element|null} The rendered Test Results Modal view.
 */
const TestResultsModal = ({ test, onClose, onPublishSuccess }) => {
    const [results, setResults] = useState([]);
    const [totalMarks, setTotalMarks] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [publishing, setPublishing] = useState(false);

    /* ══════════════════════════════════════════════════════════════
     * EFFECTS & HANDLERS
     * ══════════════════════════════════════════════════════════════ */

    useEffect(() => {
        const fetchResults = async () => {
            try {
                const response = await apiGet(`/tests/${test.id}/results`);
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error("Raw API Error:", errorText);
                    throw new Error('Failed to fetch results');
                }
                const data = await response.json();
                setResults(data.results || []);
                setTotalMarks(data.total_marks || 0);
            } catch (err) {
                console.error('Error fetching results:', err);
                setError('Failed to load results');
            } finally {
                setLoading(false);
            }
        };

        if (test?.id) {
            fetchResults();
        }
    }, [test?.id]);

    const handlePublish = async () => {
        setPublishing(true);
        try {
            const response = await apiPut(`/tests/${test.id}/publish`, {});
            if (!response.ok) throw new Error('Failed to publish');

            onPublishSuccess(test.id);
        } catch (err) {
            console.error('Publish error:', err);
            alert('Failed to publish results');
        } finally {
            setPublishing(false);
        }
    };

    const downloadCSV = () => {
        if (!results.length) return;

        const headers = ['Name', 'Institute Email', 'Roll No', 'Attendance', 'Marks Obtained', 'Total Marks', 'Violations'];
        const csvRows = [headers.join(',')];

        results.forEach(r => {
            const row = [
                `"${r.name}"`,
                `"${r.inst_email || ''}"`,
                `"${r.inst_roll_no || ''}"`,
                r.attendance,
                r.obtained_marks,
                totalMarks,
                r.violation_count
            ];
            csvRows.push(row.join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', `${test.title.replace(/\s+/g, '_')}_Results.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    if (!test) return null;

    const presentCount = results.filter(r => r.attendance === 'Present').length;
    const absentCount = results.length - presentCount;
    const avgScore = presentCount > 0
        ? (results.filter(r => r.attendance === 'Present').reduce((sum, r) => sum + (r.obtained_marks || 0), 0) / presentCount).toFixed(1)
        : 0;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-emerald-950/50 backdrop-blur-md overflow-y-auto">
            <div className="bg-white w-full max-w-7xl mx-auto my-8 flex flex-col max-h-[85vh] shadow-2xl rounded">

                {/* Header - matches Create Test modal */}
                <div className="flex items-start justify-between p-6 py-4 bg-emerald-600 text-left rounded-t">
                    <div>
                        <h3 className="text-xl font-bold text-white mb-0.5">{test.title}</h3>
                        <p className="text-xs text-emerald-100">
                            {test.start_time ? new Date(test.start_time).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + ' • ' + new Date(test.start_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase() : 'Date not set'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                        <button
                            onClick={downloadCSV}
                            disabled={loading || results.length === 0}
                            className="p-1.5 text-emerald-100 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Download CSV"
                        >
                            <FiDownload className="w-5 h-5" />
                        </button>
                        <button onClick={onClose} className="text-emerald-100 hover:text-white transition-colors">
                            <FiX className="text-2xl" />
                        </button>
                    </div>
                </div>

                {/* Summary Stats */}
                {!loading && !error && results.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pr-10 pl-7 py-6 border-b border-gray-300 bg-accent">
                        <div className="flex items-center gap-3 rounded justify-start">
                            <p className="text-sm text-muted-foreground font-medium">Total Students:</p>
                            <p className="text-sm font-bold text-foreground">{results.length}</p>
                        </div>
                        <div className="flex items-center gap-3 rounded justify-center">
                            <p className="text-sm text-muted-foreground font-medium">Present:</p>
                            <p className="text-sm font-bold text-emerald-700">{presentCount}</p>
                        </div>
                        <div className="flex items-center gap-3 rounded justify-center">
                            <p className="text-sm text-muted-foreground font-medium">Absent:</p>
                            <p className="text-sm font-bold text-red-600">{absentCount}</p>
                        </div>
                        <div className="flex items-center gap-3 rounded justify-end">
                            <p className="text-sm text-muted-foreground font-medium">Avg Score:</p>
                            <p className="text-sm font-bold text-foreground">{avgScore}<span className="text-sm font-normal text-muted-foreground"> / {totalMarks}</span></p>
                        </div>
                    </div>
                )}

                {/* Content */}
                <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
                    {loading ? (
                        <div className="animate-pulse space-y-3">
                            <div className="h-10 bg-accent rounded flex px-4 items-center gap-4">
                                <div className="h-3 w-1/5 bg-gray-200 rounded"></div>
                                <div className="h-3 w-1/5 bg-gray-200 rounded"></div>
                                <div className="h-3 w-1/6 bg-gray-200 rounded"></div>
                                <div className="h-3 w-1/6 bg-gray-200 rounded ml-auto"></div>
                            </div>
                            {[1, 2, 3, 4, 5, 6].map(i => (
                                <div key={i} className="h-14 bg-accent/50 rounded flex px-4 items-center gap-4">
                                    <div className="w-9 h-9 rounded-full bg-gray-200"></div>
                                    <div className="flex-1 space-y-2">
                                        <div className="h-3.5 w-1/3 bg-gray-200 rounded"></div>
                                        <div className="h-2.5 w-1/4 bg-gray-200 rounded"></div>
                                    </div>
                                    <div className="h-3.5 w-16 bg-gray-200 rounded hidden sm:block"></div>
                                    <div className="h-5 w-14 bg-gray-200 rounded ml-auto"></div>
                                </div>
                            ))}
                        </div>
                    ) : error ? (
                        <div className="text-red-600 text-center py-10 bg-red-50 border border-red-100 rounded">
                            <FiAlertCircle className="mx-auto text-3xl mb-2" />
                            <p className="font-medium">{error}</p>
                        </div>
                    ) : results.length === 0 ? (
                        <div className="text-center py-16">
                            <FiUsers className="mx-auto text-4xl text-muted-foreground/30 mb-3" />
                            <p className="text-muted-foreground font-medium">No students enrolled in this batch.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded border-l-4 border-emerald-600">
                            <table className="w-full text-left text-sm">
                                <tbody className="divide-y divide-gray-300 bg-accent">
                                    {results.map(r => (
                                        <tr key={r.user_id} className="hover:bg-emerald-50/40 transition-colors">
                                            <td className="px-4 py-3.5">
                                                <div className="flex items-center gap-3">
                                                    <UserAvatar
                                                        avatarUrl={r.avatar_url}
                                                        name={r.name || r.username}
                                                        size={32}
                                                    />
                                                    <div className="min-w-0">
                                                        <p className="text-foreground font-medium text-sm truncate">{r.name}</p>
                                                        <p className="text-xs text-muted-foreground font-medium truncate">@{r.username || 'user'}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3.5 text-muted-foreground text-sm hidden md:table-cell">{r.inst_email || '-'}</td>
                                            <td className="px-4 py-3.5 text-muted-foreground text-sm hidden sm:table-cell">{r.inst_roll_no || '-'}</td>
                                            <td className="px-4 py-3.5">
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold ${r.attendance === 'Present'
                                                    ? 'text-emerald-700'
                                                    : 'text-red-600'
                                                    }`}>
                                                    {r.attendance}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3.5">
                                                {r.attendance === 'Present' ? (
                                                    <span className="font-semibold text-foreground">
                                                        {r.obtained_marks}<span className="text-muted-foreground font-normal"> / {totalMarks}</span>
                                                    </span>
                                                ) : (
                                                    <span className="text-muted-foreground">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3.5 text-right">
                                                {r.attendance === 'Present' ? (
                                                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${r.violation_count > 0
                                                        ? 'bg-red-600 text-white'
                                                        : 'bg-gray-600 text-white'
                                                        }`}>
                                                        {r.violation_count}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted-foreground">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer - matches Create Test modal */}
                <div className="px-8 py-3 border-t border-gray-300 flex items-center justify-end bg-white rounded-b">
                    {!test.results_published ? (
                        <button
                            onClick={handlePublish}
                            disabled={publishing || loading}
                            className="px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white transition-all font-semibold rounded flex items-center gap-2 disabled:opacity-50 text-sm"
                        >
                            {publishing ? (
                                <><span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></span><span>Publishing...</span></>
                            ) : (
                                <>
                                    <span>Publish Results</span>
                                    <FiShare className="w-5 h-5"/>
                                </>
                            )}
                        </button>
                    ) : (
                        <div className="py-3 text-emerald-600 text-sm font-medium flex items-center gap-1 rounded">
                            <span className="tracking-wide">Results Published</span>
                            <FiCheck className="w-5 h-5 text-emerald-600" strokeWidth={3} />
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default TestResultsModal;
